/**
 * Ops Map AI Reality layers — consume MapOpsProjection (Soft≠Live).
 * Live pins/tracks vs Soft intent ghosts; never invent geo client-side.
 */
import { api } from "./api.js";
import { operatorSay } from "./operator-say.js";

const SRC = "ai-map-ops";
const LAYERS = [
  "ai-map-ops-incident",
  "ai-map-ops-evidence",
  "ai-map-ops-ghost",
  "ai-map-ops-dispatch",
  "ai-map-ops-robot",
  "ai-map-ops-robot-trail",
];

/** @param {any} mapOps */
export function mapOpsToGeoJSON(mapOps) {
  const layers = (mapOps && mapOps.layers) || {};
  const features = [];
  const pin = layers.incident_pin;
  if (pin && pin.live && pin.lat != null && pin.lng != null) {
    features.push({
      type: "Feature",
      properties: {
        kind: "incident",
        live: true,
        soft_invent: false,
        needs_you: !!pin.needs_you,
        execution_mode: pin.execution_mode || "",
        label: pin.needs_you ? "Needs You" : "AI Team",
      },
      geometry: { type: "Point", coordinates: [Number(pin.lng), Number(pin.lat)] },
    });
  }
  for (const g of layers.intent_ghosts || []) {
    if (!g || g.lat == null || g.lng == null) continue;
    features.push({
      type: "Feature",
      properties: {
        kind: "intent_ghost",
        live: false,
        soft_invent: false,
        proposal: true,
        label: g.label || "拟派遣 · 未 Live",
      },
      geometry: { type: "Point", coordinates: [Number(g.lng), Number(g.lat)] },
    });
  }
  for (const t of layers.dispatch_tracks || []) {
    if (!t || t.lat == null || t.lng == null) continue;
    features.push({
      type: "Feature",
      properties: {
        kind: "dispatch",
        live: true,
        soft_invent: false,
        status: t.status || "dispatched",
        label: t.dispatch_id || "dispatch",
      },
      geometry: { type: "Point", coordinates: [Number(t.lng), Number(t.lat)] },
    });
  }
  for (const r of layers.robot_tracks || []) {
    if (!r || r.lat == null || r.lng == null || r.soft_invent) continue;
    if (r.wgs84 === false || r.frame === "sim_local") continue;
    features.push({
      type: "Feature",
      properties: {
        kind: "robot",
        live: true,
        soft_invent: false,
        vendor: r.kind || r.vendor || "robot",
        label: r.serial || r.unit_id || "unit",
      },
      geometry: { type: "Point", coordinates: [Number(r.lng), Number(r.lat)] },
    });
    const line = r.line && r.line.coordinates;
    if (Array.isArray(line) && line.length >= 2 && r.frame !== "sim_local") {
      features.push({
        type: "Feature",
        properties: { kind: "robot_trail", live: true, soft_invent: false },
        geometry: { type: "LineString", coordinates: line },
      });
    }
  }
  for (const e of layers.evidence_markers || []) {
    if (!e || e.lat == null || e.lng == null) continue;
    if (e.soft_invent || e.qa_fixture) continue;
    features.push({
      type: "Feature",
      properties: {
        kind: "evidence",
        live: !!e.live,
        soft_invent: false,
        hardware_attested: !!e.hardware_attested,
        nearby: !!e.nearby,
        origin: !!e.origin,
        snapshot_id: e.snapshot_id || "",
        session_id: e.session_id || "",
        device_id: e.device_id || "",
        camera_id: e.camera_id || "",
        image_path: e.image_path || "",
        label: e.label || (e.nearby ? "nearby remux" : "live at point"),
      },
      geometry: { type: "Point", coordinates: [Number(e.lng), Number(e.lat)] },
    });
  }
  return { type: "FeatureCollection", features };
}

export function ensureAiMapOpsLayers(map) {
  if (!map || !map.getStyle) return;
  if (!map.getSource(SRC)) {
    map.addSource(SRC, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }
  if (!map.getLayer("ai-map-ops-ghost")) {
    map.addLayer({
      id: "ai-map-ops-ghost",
      type: "circle",
      source: SRC,
      filter: ["==", ["get", "kind"], "intent_ghost"],
      paint: {
        "circle-radius": 10,
        "circle-color": "#94a3b8",
        "circle-opacity": 0.35,
        "circle-stroke-width": 1.5,
        "circle-stroke-color": "#64748b",
        "circle-stroke-opacity": 0.8,
      },
    });
  }
  if (!map.getLayer("ai-map-ops-evidence")) {
    map.addLayer({
      id: "ai-map-ops-evidence",
      type: "circle",
      source: SRC,
      filter: ["==", ["get", "kind"], "evidence"],
      paint: {
        "circle-radius": [
          "case",
          ["==", ["get", "origin"], true],
          10,
          ["==", ["get", "nearby"], true],
          7,
          8,
        ],
        "circle-color": [
          "case",
          ["==", ["get", "origin"], true],
          "#26D9B0",
          ["==", ["get", "nearby"], true],
          "#F0C36A",
          ["==", ["get", "hardware_attested"], true],
          "#22d3ee",
          "#38bdf8",
        ],
        "circle-opacity": 0.9,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#0e7490",
      },
    });
  }
  if (!map.__aiMapOpsFrameClick) {
    map.__aiMapOpsFrameClick = true;
    const lib = globalThis.maplibregl
      || (map.constructor && map.constructor.Popup ? { Popup: map.constructor.Popup } : null);
    map.on("click", "ai-map-ops-evidence", (ev) => {
      const f = ev.features && ev.features[0];
      if (!f || !lib || !lib.Popup) return;
      const p = f.properties || {};
      const path = String(p.image_path || "").trim();
      const src = path.startsWith("http") ? path : (path ? `/api${path.startsWith("/") ? path : `/${path}`}` : "");
      const label = String(p.label || "live at point");
      const seat = p.origin ? "Live at the point" : (p.nearby ? "Nearby live" : label);
      const html = src
        ? `<div class="ai-map-ops-frame"><img src="${src}" alt="" width="220" style="display:block;max-width:220px;border-radius:6px"/><div class="ai-map-ops-frame__cap">${seat}</div></div>`
        : `<div class="ai-map-ops-frame ai-map-ops-frame--live"><b>${seat}</b><small>Realtime layer is on the map · screenshot is a separate skill</small></div>`;
      new lib.Popup({ closeButton: true, maxWidth: "260px" })
        .setLngLat(ev.lngLat)
        .setHTML(html)
        .addTo(map);
    });
    map.on("mouseenter", "ai-map-ops-evidence", () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "ai-map-ops-evidence", () => {
      map.getCanvas().style.cursor = "";
    });
  }
  if (!map.getLayer("ai-map-ops-dispatch")) {
    map.addLayer({
      id: "ai-map-ops-dispatch",
      type: "circle",
      source: SRC,
      filter: ["==", ["get", "kind"], "dispatch"],
      paint: {
        "circle-radius": 9,
        "circle-color": "#22c55e",
        "circle-opacity": 0.9,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#15803d",
      },
    });
  }
  if (!map.getLayer("ai-map-ops-robot-trail")) {
    map.addLayer({
      id: "ai-map-ops-robot-trail",
      type: "line",
      source: SRC,
      filter: ["==", ["get", "kind"], "robot_trail"],
      paint: {
        "line-color": "#66efd2",
        "line-width": 3,
        "line-opacity": 0.85,
      },
    });
  }
  if (!map.getLayer("ai-map-ops-robot")) {
    map.addLayer({
      id: "ai-map-ops-robot",
      type: "circle",
      source: SRC,
      filter: ["==", ["get", "kind"], "robot"],
      paint: {
        "circle-radius": 10,
        "circle-color": "#66efd2",
        "circle-opacity": 0.92,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#0f766e",
      },
    });
  }
  if (!map.getLayer("ai-map-ops-incident")) {
    map.addLayer({
      id: "ai-map-ops-incident",
      type: "circle",
      source: SRC,
      filter: ["==", ["get", "kind"], "incident"],
      paint: {
        "circle-radius": [
          "case",
          ["==", ["get", "needs_you"], true],
          14,
          11,
        ],
        "circle-color": [
          "case",
          ["==", ["get", "needs_you"], true],
          "#f97316",
          "#ef4444",
        ],
        "circle-opacity": 0.92,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#fff",
      },
    });
  }
}

export function syncAiMapOpsLayers(map, mapOps) {
  if (!map) return { ok: false, reason: "no_map" };
  ensureAiMapOpsLayers(map);
  const src = map.getSource(SRC);
  if (!src || typeof src.setData !== "function") return { ok: false, reason: "no_source" };
  const fc = mapOpsToGeoJSON(mapOps);
  src.setData(fc);
  return {
    ok: true,
    revision: mapOps && mapOps.revision,
    soft_invent: false,
    n: fc.features.length,
  };
}

export function clearAiMapOpsLayers(map) {
  if (!map) return;
  const src = map.getSource(SRC);
  if (src && typeof src.setData === "function") {
    src.setData({ type: "FeatureCollection", features: [] });
  }
}

/**
 * Pull MapOps for an incident and paint Soft≠Live layers.
 * @returns {Promise<{ok:boolean, revision?:any, n?:number, reason?:string, liveSeats?:any[]}>}
 */
export function liveSeatsFromMapOps(mapOps) {
  const layers = (mapOps && mapOps.layers) || {};
  const out = [];
  const seen = new Set();
  for (const e of layers.evidence_markers || []) {
    if (!e || e.soft_invent || e.qa_fixture) continue;
    const sessionId = String(e.session_id || "").trim();
    const deviceId = String(e.device_id || "").trim();
    const cameraId = String(e.camera_id || "").trim();
    const sourceId = String(e.source_id || "").trim();
    if (!sessionId || !deviceId) continue;
    /* Unique by camera seat, never board SN. Two cameras on one board stay two chips. */
    const id = cameraId || sourceId || sessionId;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const lng = e.lng != null ? Number(e.lng) : null;
    const lat = e.lat != null ? Number(e.lat) : null;
    const ll = Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
    out.push({
      id,
      cameraId,
      deviceId,
      sessionId,
      live: true,
      nearby: !!e.nearby,
      origin: !!e.origin || (!e.nearby && out.length === 0),
      livePlayer: "webcodecs_au",
      lat,
      lng,
      ll,
      label: e.label || (e.nearby ? "Nearby" : "Live") || cameraId || deviceId,
    });
  }
  return out.slice(0, 3);
}

export function closeWaitCopy(reason) {
  const r = String(reason || "").trim();
  if (r === "wait_nearby_live") return "Waiting for nearby live to play";
  if (r === "wait_live_loop") return "Waiting for each live camera to close its proof";
  if (r === "live_loops_open") return "Waiting for each live camera to close its proof";
  if (r === "wait_record_proof" || r === "wait_record_skill") return "Waiting for a recording attach";
  if (r === "second_source_missing") return "Waiting for an independent snapshot";
  if (r === "dispatch_still_planned") return "Dispatch is still on the plan";
  return operatorSay(r, "Waiting for proof");
}

export function loopHudFromMapOps(mapOps) {
  const loop = (mapOps && mapOps.loop && typeof mapOps.loop === "object") ? mapOps.loop : {};
  const seats = liveSeatsFromMapOps(mapOps);
  const liveN = Number(loop.live_n != null ? loop.live_n : seats.length) || 0;
  const playingN = Number(loop.playing_n != null ? loop.playing_n : seats.filter((s) => s && s.sessionId).length) || 0;
  const closedN = Number(loop.closed_n || 0) || 0;
  const loopSeats = Array.isArray(loop.seats)
    ? loop.seats.filter((s) => s && typeof s === "object" && s.soft_invent !== true).slice(0, 6)
    : [];
  const total = Math.max(0, Math.min(6, liveN || loopSeats.length));
  const marks = [];
  for (let i = 0; i < total; i++) {
    const fromLoop = loopSeats[i];
    const s = seats[i];
    marks.push({
      origin: !!(fromLoop && fromLoop.origin) || !!(s && s.origin),
      nearby: !!(fromLoop && fromLoop.nearby) || !!(s && s.nearby),
      playing: !!(fromLoop && fromLoop.playing) || !!(s && s.sessionId),
      closed: !!(fromLoop && fromLoop.closed) || (!fromLoop && i < closedN),
      camera_id: String((fromLoop && fromLoop.camera_id) || (s && (s.cameraId || s.camera_id)) || ""),
    });
  }
  return {
    incident_id: String((mapOps && mapOps.incident_id) || ""),
    live_n: liveN,
    nearby_n: Number(loop.nearby_n || 0) || 0,
    playing_n: playingN,
    closed_n: closedN,
    snapshot: loop.snapshot === true,
    record: loop.record === true,
    close: loop.close === true,
    close_ready: loop.close_ready === true,
    close_wait: String(loop.close_wait || ""),
    origin_playing: loop.origin_playing === true || seats.some((s) => s && s.origin && s.sessionId),
    seats: marks,
    soft_invent: false,
  };
}

export async function fetchAndSyncAiMapOps(map, { incidentId, tenantId = "" } = {}) {
  const iid = String(incidentId || "").trim();
  if (!map || !iid) return { ok: false, reason: "missing_incident", liveSeats: [], loop: loopHudFromMapOps(null) };
  try {
    const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
    const path = `/ai/team/incidents/${encodeURIComponent(iid)}/map-ops${q}`;
    const res = await api(path);
    const mapOps = (res && res.map_ops) || res || {};
    if (!mapOps || mapOps.ok === false) {
      clearAiMapOpsLayers(map);
      return { ok: false, reason: (mapOps && mapOps.reason) || "no_map_ops", liveSeats: [], loop: loopHudFromMapOps(null) };
    }
    const painted = syncAiMapOpsLayers(map, mapOps);
    return { ...painted, liveSeats: liveSeatsFromMapOps(mapOps), loop: loopHudFromMapOps(mapOps) };
  } catch (_err) {
    return { ok: false, reason: "fetch_failed", liveSeats: [], loop: loopHudFromMapOps(null) };
  }
}

export function setAiMapOpsVisibility(map, visible) {
  if (!map) return;
  const vis = visible ? "visible" : "none";
  for (const id of LAYERS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
  }
}
